import type { MDXContent } from 'mdx/types'
import { use } from 'react'
import { registry } from '#course-catalog'
import cache from '#course-module-cache'
import { useLocation } from '#router'
import { useCodeCopyButtons } from '@/hooks/use-code-copy-buttons'
import ErrorFallback from './error-fallback'

function isPromise(value: unknown): value is Promise<unknown> {
  return Promise.resolve(value) === value
}

interface Props {
  pathname?: string
}

function useCourseContent(props: Props) {
  const location = useLocation()
  const pathname = props.pathname ?? location.pathname
  const metadata = registry.get(pathname)
  if (!metadata) return ErrorFallback

  const contentOrPromise = cache.get(pathname)

  // Not in cache - initiate dynamic import
  if (!contentOrPromise) {
    const promise = import(/* @vite-ignore */ metadata.import)
      .then(({ default: result }: { default: MDXContent }) => {
        cache.set(metadata.href, result)
        return result
      })
      .catch(() => ErrorFallback)

    cache.set(pathname, promise)
    return use(promise) // Suspend until Promise resolves
  }

  // In cache as Promise - suspend until resolved
  if (isPromise(contentOrPromise)) return use(contentOrPromise)

  // In cache as component - render immediately
  return contentOrPromise
}

export default function CourseContent(props: Props) {
  const location = useLocation()
  const Content = useCourseContent(props)

  // Add copy buttons to code blocks after content renders. CourseContent
  // persists across course navigation (no key), so key the effect on the
  // rendered content's pathname to re-attach buttons on every page.
  useCodeCopyButtons(props.pathname ?? location.pathname)

  return (
    <article>
      <Content />
    </article>
  )
}
